<template>
  <section class="widget-card" :style="{ '--widget-accent': accent }">
    <header class="widget-header">
      <div class="widget-title-group">
        <span class="widget-icon">
          <Icon :icon="icon" width="18" height="18" />
        </span>
        <div class="widget-title-text">
          <h6 class="widget-title">{{ title }}</h6>
          <p v-if="subtitle" class="widget-subtitle">{{ subtitle }}</p>
        </div>
      </div>
      <div class="widget-header-actions">
        <slot name="actions" />
        <router-link v-if="linkTo" :to="linkTo" class="widget-link">
          {{ linkLabel }}
          <Icon icon="mdi:arrow-right" width="14" height="14" />
        </router-link>
      </div>
    </header>

    <div class="widget-body">
      <div v-if="isLoading" class="widget-loading">
        <LoadingBars />
      </div>
      <slot v-else />
    </div>
  </section>
</template>

<script setup>
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";

defineOptions({
  name: "DashboardWidgetCard",
});

defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: "" },
  icon: { type: String, required: true },
  accent: { type: String, default: "#4a3aff" },
  linkTo: { type: Object, default: null },
  linkLabel: { type: String, default: "View all" },
  isLoading: { type: Boolean, default: false },
});
</script>

<style scoped>
.widget-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 12px;
}

.widget-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.widget-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex-shrink: 0;
  color: var(--widget-accent);
  background: color-mix(in srgb, var(--widget-accent) 12%, #ffffff);
}

.widget-title-text {
  min-width: 0;
}

.widget-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
}

.widget-subtitle {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.widget-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.widget-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--widget-accent);
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background 0.15s ease;
}

.widget-link:hover {
  background: color-mix(in srgb, var(--widget-accent) 10%, #ffffff);
}

.widget-body {
  flex: 1;
  padding: 4px 20px 20px;
  position: relative;
}

.widget-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}
</style>
